import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store/index.js'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'login',
    meta: { layout: 'login' },
    alias: [
      '/kontakt',
      '/agb',
      '/datenschutz',
      '/impressum'
    ],
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/bestaetigungs-code',
    name: 'authCode',
    meta: { layout: 'login' },
    beforeEnter (to, from, next) {
      isAuthCodePageVisible(next)
    },
    component: () => import('@/views/AuthCode.vue')
  },
  {
    path: '/telefonnummer-aendern',
    name: 'authPhoneNumber',
    meta: { layout: 'login' },
    beforeEnter (to, from, next) {
      isAuthCodePageVisible(next)
    },
    component: () => import('@/views/AuthChangePhoneNumber.vue')
  },
  {
    path: '/registrieren',
    name: 'register',
    meta: { layout: 'login' },
    component: () => import('@/views/Register.vue')
  },
  {
    path: '/einladung-ablehnen',
    name: 'declinePermission',
    meta: { layout: 'login' },
    component: () => import('@/views/PermissionDecline.vue')
  },
  {
    path: '/passwort-vergessen',
    name: 'forgotpassword',
    meta: { layout: 'login' },
    component: () => import('@/views/ForgotPassword.vue')
  },
  {
    path: '/passwort-zuruecksetzen/:token',
    name: 'resetpassword',
    meta: { layout: 'login' },
    component: () => import('@/views/ResetPassword.vue')
  },
  {
    path: '/foerdergeldrechner',
    name: 'foerdergeldrechner',
    meta: { layout: 'login' },
    component: () => import('@/views/CalculatorPublic.vue')
  },
  {
    path: '/logout',
    name: 'logout',
    meta: { authRequired: true, layout: 'login' },
    component: () => import('@/views/Logout.vue')
  },
  {
    path: '/uebersicht/:modalMethod?/:tabName?/:draftId?/:gesuchId?',
    name: 'uebersicht',
    meta: { authRequired: true },
    component: () => import('@/views/Overview.vue')
  },
  {
    path: '/gesuch/:applicationId',
    name: 'application-detail',
    meta: { authRequired: true },
    component: () => import('@/views/ApplicationDetail.vue')
  },
  {
    path: '/person',
    name: 'personen',
    meta: { authRequired: true },
    component: () => import('@/views/Persons.vue')
  },
  {
    path: '/person/erstellen',
    name: 'person-create',
    meta: { authRequired: true },
    component: () => import('@/components/person/PersonAddFormWrapper.vue')
  },
  {
    path: '/person/:personId',
    name: 'person-detail',
    meta: { authRequired: true },
    component: () => import('@/components/person/PersonDetail.vue')
  },
  {
    path: '/person/:personId/bearbeiten',
    name: 'person-edit',
    meta: { authRequired: true },
    component: () => import('@/components/person/PersonEdit.vue')
  },
  {
    path: '/gebaeude',
    name: 'property',
    meta: { authRequired: true },
    component: () => import('@/views/Property.vue')
  },
  {
    path: '/gebaeude/erstellen',
    name: 'property-create',
    meta: { authRequired: true },
    component: () => import('@/components/property/PropertyAddFormWrapper.vue')
  },
  {
    path: '/gebaeude/:propertyId',
    name: 'property-detail',
    meta: { authRequired: true },
    component: () => import('@/components/property/PropertyDetail.vue')
  },
  {
    path: '/gebaeude/:propertyId/bearbeiten',
    name: 'property-edit',
    meta: { authRequired: true },
    component: () => import('@/components/property/PropertyEdit.vue')
  },
  {
    path: '/foerdergeld-rechner',
    name: 'foerdergeld-rechner',
    meta: { authRequired: true },
    component: () => import('@/views/Calculator.vue')
  },
  {
    path: '/firmenkonto',
    name: 'firmenkonto',
    meta: { authRequired: true },
    component: () => import('@/views/Organisation.vue')
  },
  {
    path: '/hilfe',
    name: 'hilfe',
    meta: { authRequired: true },
    component: () => import('@/views/Help.vue')
  },
  {
    path: '/einstellungen',
    name: 'einstellungen',
    meta: { authRequired: true },
    component: () => import('@/views/Settings.vue')
  },
  {
    path: '/Account/LogOn',
    redirect: {
      name: 'login'
    }
  },
  {
    path: '/nichtunterstuetzt',
    name: 'nichtunterstuetzt',
    meta: { layout: 'empty-page' },
    component: () => import('@/views/NotSupportedBrowser.vue')
  },
  {
    path: '*',
    meta: { layout: 'empty-page' },
    component: () => import('@/views/404.vue')
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

/*
  Make the route authCode and authPhoneNumber accessible when the user is authenticated
  but not logged in to change the phoneNumber and fill in the authentication code
 */
const isAuthCodePageVisible = (next) => {
  const { isAuthCodePageVisible } = store.state.user

  if (!isAuthCodePageVisible) {
    next({ name: 'login' })
  } else {
    next()
  }
}

/*
  Used to persist the invitecode until user is loged in.
  User always gets logged out
 */
const handleInvite = async (inviteCode, routeName, next) => {
  store.commit('permission/SET_INVITE_CODE', decodeURIComponent(inviteCode))

  await store.dispatch('user/LOGOUT')

  if (routeName === 'register') {
    next({ name: 'register' })
  } else {
    next({ name: 'login' })
  }
}

/*
  Used to detect Interent-Explorer and display a not
  supported page
*/
const isIE = () => {
  return window.navigator.userAgent.match(/(MSIE|Trident)/)
}

router.beforeEach(async (to, from, next) => {
  const { name: routeName } = to
  const { inviteCode } = to.query
  const { loggedInState } = store.state.user

  if (isIE() && routeName !== 'nichtunterstuetzt') {
    next({ name: 'nichtunterstuetzt' })
  }

  if (inviteCode && routeName !== 'declinePermission') {
    await handleInvite(inviteCode, routeName, next)
  }

  if (routeName === 'declinePermission') { // Make declinePermission always accessible
    next()
  } else if (to.meta.authRequired && !loggedInState) {
    next({ name: 'login' })
  } else if (!to.meta.authRequired && loggedInState) {
    next({ name: 'uebersicht' })
  } else {
    next()
  }
})

export default router
